import { numericFormatter } from 'react-number-format';

import type { Maybe } from '../../apis/graphql/types';

export const numberToLocaledString = (figure: number): string => {
  return Number(figure).toLocaleString();
};

export const getNumberToSum = (number?: Maybe<number>, decimals = 2): string | null => {
  return number ? '$' + numericFormatter(number.toFixed(decimals), { thousandSeparator: true }) : null;
};

export const getTargetImpressions = (
  deliveredImpressions?: Maybe<number>,
  targetImpressions?: Maybe<number> | undefined
): string => {
  const delivered = getFormattedNumberWithThousandSeparator(deliveredImpressions);
  const target = getFormattedNumberWithThousandSeparator(targetImpressions);
  return `${delivered}/${target}`;
};

export const getFormattedNumberWithThousandSeparator = (value?: Maybe<number>): string => {
  return value ? numericFormatter(String(value), { thousandSeparator: true }) : '-';
};

type FormatNumberProps = {
  figure: number;
  dollarValue?: boolean;
  percentageValue?: boolean;
  figuresAfterComma?: number;
  preserveDecimals?: boolean;
};

/**
 * Formats a number with optional dollar sign, percentage, and configurable decimal places.
 *
 * @param figure - The number to format
 * @param dollarValue - If true, prepends a dollar sign ($)
 * @param percentageValue - If true, appends a percentage sign (%)
 * @param figuresAfterComma - Number of decimal places (default: 2)
 * @param preserveDecimals - Optional: When true, enforces fixed decimal places with trailing zeros.
 *                           When false (default), trailing zeros are removed for backward compatibility.
 *                           Example: 1000000 -> "$1,000,000.00" (true) vs "$1,000,000" (false)
 * @returns Formatted number as a string
 *
 * @example
 * formatNumber({ figure: 1000000, dollarValue: true })
 * // Returns: "$1,000,000"
 *
 * @example
 * formatNumber({ figure: 1000000, dollarValue: true, preserveDecimals: true })
 * // Returns: "$1,000,000.00"
 */
export const formatNumber = ({
  figure,
  dollarValue,
  percentageValue,
  figuresAfterComma = 2,
  preserveDecimals = false,
}: FormatNumberProps): string => {
  if (preserveDecimals) {
    const formatted = figure.toLocaleString('en-US', {
      minimumFractionDigits: figuresAfterComma,
      maximumFractionDigits: figuresAfterComma,
    });
    return `${dollarValue ? '$' : ''}${formatted}${percentageValue ? '%' : ''}`;
  }

  const numberToFixed = figure.toFixed(figuresAfterComma);

  return `${dollarValue ? '$' : ''}${Number(numberToFixed).toLocaleString()}${percentageValue ? '%' : ''}`;
};

export const formatToNonMicros = (num: number): number => num / 1000000;

export const roundToTwoDecimalPlaces = (value: number | undefined | null): number | undefined | null => {
  return value ? Math.round(value * 100) / 100 : value;
};
